import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  addToast,
  Button,
  Checkbox,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useState } from "react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { api } from "@/lib/api-client";

export function RemoveFixedMemberModal({
  isOpen,
  onClose,
  fixedAppointmentId,
  member,
  onRemoved,
}: {
  isOpen: boolean;
  onClose: () => void;
  fixedAppointmentId: number;
  member: { customerId: number; name: string } | null;
  onRemoved?: () => void;
}) {
  const [cancelFutureOccurrences, setCancelFutureOccurrences] = useState(true);

  // Reset to the default (checked) each time the modal opens.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setCancelFutureOccurrences(true);
  }

  const { mutateAsync: removeMember, isPending } =
    api.fixedAppointment.useDeleteFixedAppointmentMember();

  async function handleRemove() {
    if (!member) return;
    try {
      const { cancelledCount } = await removeMember({
        fixedAppointmentId,
        customerId: member.customerId,
        body: { cancelFutureOccurrences },
      });
      addToast({
        title: cancelFutureOccurrences
          ? t`Stranka odstranjena iz prihodnjih terminov`
          : t`Stranka ni več redna`,
        description:
          cancelFutureOccurrences && cancelledCount > 0
            ? t`Odpovedanih ${cancelledCount} terminov.`
            : undefined,
        color: "success",
      });
      onRemoved?.();
      onClose();
    } catch {
      addToast({
        title: t`Napaka pri odstranjevanju redne stranke`,
        color: "danger",
      });
    }
  }

  return (
    <LimeModal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader>
          <Trans>Odstrani redno stranko</Trans>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-3">
            <p className="text-default-600 text-sm">
              <Trans>
                Ali želite odstraniti stranko{" "}
                <span className="font-medium">{member?.name}</span> kot redno
                stranko?
              </Trans>
            </p>
            <Checkbox
              isSelected={cancelFutureOccurrences}
              onValueChange={setCancelFutureOccurrences}
              size="sm"
            >
              <Trans>
                Stranko odstrani tudi iz vseh prihodnjih ponovitev tega rednega
                termina.
              </Trans>
            </Checkbox>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            data-identifier="fixed-appt-remove-member-confirm"
            color="danger"
            onPress={handleRemove}
            isLoading={isPending}
          >
            <Trans>Odstrani</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
}
